import { create } from 'zustand';
import { CalendarService } from '../services/calendar.service';
import {
  CalendarEvent,
  CreateCategoryData,
  CreateEventData,
  EventCategory,
  UpdateCategoryData,
  UpdateEventData
} from '../types/calendar.types';

interface CalendarStore {
  // Data
  events: CalendarEvent[];
  categories: EventCategory[];
  currentEvent: CalendarEvent | null;

  // Loading states
  loading: boolean;
  loadingEvent: boolean;
  initialized: boolean;
  error: string | null;

  // Filters
  activeCategory: string | null;

  // Actions
  fetchCalendarData: (forceRefresh: boolean) => Promise<void>;
  fetchEvent: (eventId: string) => Promise<CalendarEvent | null>;
  createEvent: (data: CreateEventData) => Promise<CalendarEvent | null>;
  updateEvent: (eventId: string, data: UpdateEventData) => Promise<CalendarEvent | null>;
  deleteEvent: (eventId: string) => Promise<boolean>;
  createCategory: (data: CreateCategoryData) => Promise<EventCategory | null>;
  updateCategory: (categoryId: string, data: UpdateCategoryData) => Promise<EventCategory | null>;
  deleteCategory: (categoryId: string) => Promise<boolean>;
  toggleCategoryFilter: (categoryId: string) => void;
  clearCategoryFilter: () => void;
  reset: () => void;
}

export const useCalendarStore = create<CalendarStore>((set, get) => ({
  // Initial state
  events: [],
  categories: [],
  currentEvent: null,
  loading: false,
  loadingEvent: false,
  initialized: false,
  error: null,
  activeCategory: null,

  // Fetch all calendar data
  fetchCalendarData: async (forceRefresh: boolean = false) => {
    // Skip if already initialized and data exists
    if (get().initialized && get().events.length > 0 && !get().loading && !forceRefresh) {
      return;
    }

    try {
      set({ loading: true, error: null });
      const data = await CalendarService.getCalendarData();
      set({
        events: data.events,
        categories: data.categories,
        initialized: true,
        loading: false
      });
    } catch (err) {
      console.error('Error fetching calendar data:', err);
      set({
        error: 'Error fetching calendar data',
        loading: false
      });
    }
  },

  // Fetch a specific event by ID
  fetchEvent: async (eventId: string) => {
    try {
      // Check if we already have this event with complete data
      const existingEvent = get().events.find(e => e.id === eventId);
      if (existingEvent && existingEvent.attendees) {
        set({ currentEvent: existingEvent });
        return existingEvent;
      }

      set({ loadingEvent: true });
      const event = await CalendarService.getEvent(eventId);
      set({ currentEvent: event, loadingEvent: false });

      // Update the event in the events array if it exists
      set(state => ({
        events: state.events.map(e => e.id === eventId ? event : e)
      }));

      return event;
    } catch (err) {
      console.error('Error fetching event:', err);
      set({ loadingEvent: false });
      return null;
    }
  },

  // Create a new event
  createEvent: async (data: CreateEventData) => {
    try {
      const newEvent = await CalendarService.createEvent(data);
      set(state => ({
        events: [...state.events, newEvent]
      }));
      return newEvent;
    } catch (err) {
      console.error('Error creating event:', err);
      return null;
    }
  },

  // Update an existing event
  updateEvent: async (eventId: string, data: UpdateEventData) => {
    try {
      const updatedEvent = await CalendarService.updateEvent(eventId, data);
      set(state => ({
        events: state.events.map(event =>
          event.id === eventId ? updatedEvent : event
        ),
        currentEvent: state.currentEvent?.id === eventId ? updatedEvent : state.currentEvent
      }));
      return updatedEvent;
    } catch (err) {
      console.error('Error updating event:', err);
      return null;
    }
  },

  // Delete an event
  deleteEvent: async (eventId: string) => {
    try {
      await CalendarService.deleteEvent(eventId);
      set(state => ({
        events: state.events.filter(event => event.id !== eventId),
        currentEvent: state.currentEvent?.id === eventId ? null : state.currentEvent
      }));
      return true;
    } catch (err) {
      console.error('Error deleting event:', err);
      return false;
    }
  },

  // Create a new category
  createCategory: async (data: CreateCategoryData) => {
    try {
      const newCategory = await CalendarService.createCategory(data);
      set(state => ({
        categories: [...state.categories, newCategory]
      }));
      return newCategory;
    } catch (err) {
      console.error('Error creating category:', err);
      return null;
    }
  },

  // Update a category
  updateCategory: async (categoryId: string, data: UpdateCategoryData) => {
    try {
      const updatedCategory = await CalendarService.updateCategory(categoryId, data);
      set(state => ({
        categories: state.categories.map(category =>
          category.id === categoryId ? updatedCategory : category
        )
      }));
      return updatedCategory;
    } catch (err) {
      console.error('Error updating category:', err);
      return null;
    }
  },

  // Delete a category
  deleteCategory: async (categoryId: string) => {
    try {
      await CalendarService.deleteCategory(categoryId);
      set(state => ({
        categories: state.categories.filter(category => category.id !== categoryId),
        activeCategory: state.activeCategory === categoryId ? null : state.activeCategory
      }));
      return true;
    } catch (err) {
      console.error('Error deleting category:', err);
      return false;
    }
  },

  // Toggle active category filter
  toggleCategoryFilter: (categoryId: string) => {
    set(state => ({
      activeCategory: state.activeCategory === categoryId ? null : categoryId
    }));
  },

  // Clear active category filter
  clearCategoryFilter: () => {
    set({ activeCategory: null });
  },

  // Reset store
  reset: () => {
    set({
      events: [],
      categories: [],
      currentEvent: null,
      loading: false,
      loadingEvent: false,
      initialized: false,
      error: null,
      activeCategory: null
    });
  }
}));
